import type { AxiosRequestConfig } from 'axios';
import { Http } from '@mygpt/app/http';

// 跳过全局错误弹窗（后端用 400 表达"测试失败"这类业务结果，由调用方自己展示）。
// skipErrorNotification 的模块增强在 apis/types/axios.d.ts，admin 的 tsconfig 不一定收进来，这里显式断言
const SILENT = { skipErrorNotification: true } as AxiosRequestConfig;

// ============ bot 侧（連携アプリ：回复环配置） ============

// 同じ接続を使っている他の bot（対象フォームが重なると同じチケットに 2 つのドラフトが書かれる）
export interface ZendeskConnectionSharer {
  robot_id: string;
  robot_name?: string | null;
  target_form_ids: (number | string)[];
  form_overlap: boolean;
}

// 同じ接続を知識取込に使っているナレッジベース（用途「ナレッジ取込」／削除時の依存表示）
export interface ZendeskImportSource {
  dataset_id: string;
  dataset_name?: string | null;
  status: string;
  last_run_at?: string | null;
}

export interface ZendeskAuthentication {
  api_token_configured: boolean;
  oauth_configured: boolean;
  oauth_available: boolean;
  oauth_expires_at?: string | null;
  oauth_last_error?: string | null;
}

export interface ZendeskConnection {
  id: string;
  subdomain: string;
  admin_email: string;
  friendly_name?: string | null;
  status: string;
  authentication?: ZendeskAuthentication;
  last_tested_at?: string | null;
  used_by?: ZendeskConnectionSharer[];
  used_by_datasets?: ZendeskImportSource[];
}

// 接続二选一：connection_id 复用已有 / subdomain + admin_email + api_token 新建
export interface ZendeskIntegrationConfig {
  connection_id?: string;
  subdomain?: string;
  admin_email?: string;
  api_token?: string;
  friendly_name?: string;
  target_form_ids: number[];
  signature_patterns: string[];
  draft_note_enabled: boolean;
  show_sources_in_note: boolean;
  sink_dataset_id?: string | null;
  register_webhook: boolean;
}

export interface CreateZendeskResponse {
  id: string;
  connection_id: string;
  webhook_url: string;
  webhook_registered: boolean;
  webhook_error?: string | null;
  shared_with?: ZendeskConnectionSharer[];
  form_overlap?: boolean;
}

export interface ZendeskTestResult {
  ok: boolean;
  message: string;
}

export interface ZendeskWebhookResult {
  webhook_registered: boolean;
  webhook_id?: string | null;
  webhook_error?: string | null;
}

export const getZendeskConnections = (robotId: string) => {
  const { get } = Http.use();
  return get<{ connections: ZendeskConnection[] }>(`/robots/${robotId}/integrations/zendesk/connections`);
};

export const createZendeskIntegration = (robotId: string, config: ZendeskIntegrationConfig) => {
  const { post } = Http.use();
  return post<CreateZendeskResponse>(`/robots/${robotId}/integrations/zendesk`, config);
};

export const deleteZendeskIntegration = (robotId: string, configId: string) => {
  const { delete: deleteHttp } = Http.use();
  return deleteHttp(`/robots/${robotId}/integrations/zendesk/${configId}`);
};

// 接続テスト：后端失败时返 400 + {ok:false, message}，这里吞掉全局错误弹窗自己展示
export const testZendeskIntegration = async (robotId: string, configId: string): Promise<ZendeskTestResult> => {
  const { post } = Http.use();
  try {
    const response = await post<ZendeskTestResult>(
      `/robots/${robotId}/integrations/zendesk/${configId}/test`,
      {},
      SILENT
    );
    return response.data;
  } catch (error: any) {
    const data = error?.response?.data;
    if (data && typeof data.ok === 'boolean') return data as ZendeskTestResult;
    return { ok: false, message: data?.detail || error?.message || 'unknown error' };
  }
};

export const registerZendeskWebhook = async (robotId: string, configId: string): Promise<ZendeskWebhookResult> => {
  const { post } = Http.use();
  try {
    const response = await post<ZendeskWebhookResult>(
      `/robots/${robotId}/integrations/zendesk/${configId}/register-webhook`,
      {},
      SILENT
    );
    return response.data;
  } catch (error: any) {
    const data = error?.response?.data;
    if (data && typeof data.webhook_registered === 'boolean') return data as ZendeskWebhookResult;
    return { webhook_registered: false, webhook_error: data?.detail || error?.message || 'unknown error' };
  }
};

// 設定だけ変える（接続と webhook は作り直さない）。status=paused で用途「回答ドラフト」を一時停止
export interface ZendeskConfigPatch {
  status?: 'active' | 'paused';
  target_form_ids?: number[];
  signature_patterns?: string[];
  draft_note_enabled?: boolean;
  show_sources_in_note?: boolean;
  sink_dataset_id?: string | null;
}

export const patchZendeskIntegration = (robotId: string, configId: string, body: ZendeskConfigPatch) => {
  const { patch } = Http.use();
  return patch<Record<string, unknown>>(`/robots/${robotId}/integrations/zendesk/${configId}`, body, SILENT);
};

export interface ZendeskFormRow {
  id: number;
  name: string;
  active: boolean;
  end_user_visible: boolean;
  recent_tickets: number;
  included: boolean;
}

// 対象フォームを名前で選ぶための一覧 + 直近 N 日のチケットのカバー範囲
export interface ZendeskFormCoverage {
  forms: ZendeskFormRow[];
  all_forms: boolean;
  recent_total: number;
  recent_covered: number;
  recent_unknown_form: number;
  unknown_selected: number[];
  days: number;
  connection_id: string;
}

export const getZendeskForms = (
  robotId: string,
  params: { connection_id?: string; selected?: string; days?: number } = {}
) => {
  const { get } = Http.use();
  return get<ZendeskFormCoverage>(`/robots/${robotId}/integrations/zendesk/forms`, { params, ...SILENT });
};

export interface ZendeskSignaturePreview {
  normalized: string;
  cleaned: string;
  removed: string;
  matched_pattern: string | null;
  invalid_patterns: string[];
}

// 署名パターンの試し打ち（保存不要）
export const testZendeskSignature = (robotId: string, body: { sample: string; patterns: string[] }) => {
  const { post } = Http.use();
  return post<ZendeskSignaturePreview>(`/robots/${robotId}/integrations/zendesk/signature-test`, body, SILENT);
};

export const updateZendeskWebhookSecret = (
  robotId: string,
  configId: string,
  body: { signing_secret: string; webhook_id?: string }
) => {
  const { put } = Http.use();
  return put<ZendeskWebhookResult>(`/robots/${robotId}/integrations/zendesk/${configId}/webhook-secret`, body);
};

// ============ dataset 侧（サポートチケット：知识环数据源） ============

export type ZendeskSourceStatus = 'idle' | 'queued' | 'importing' | 'distilling' | 'imported' | 'error';

export interface ZendeskSourceStats {
  pii_masked?: number;
  started_at?: string;
  finished_at?: string;
  tickets_seen?: number;
  skipped_status?: number;
  skipped_guard?: number;
  items_created?: number;
  items_updated?: number;
  pages?: number;
  distill_items?: number;
  distill_generalizable?: number;
  distill_llm_failed?: number;
  dedup_batch_removed?: number;
  dedup_existing_removed?: number;
  faqs_created?: number;
}

export interface ZendeskSource {
  authentication?: ZendeskAuthentication;
  id: string;
  dataset_id: string;
  connection_id: string;
  subdomain: string;
  admin_email: string;
  friendly_name?: string | null;
  connection_status: string;
  import_since?: string | null;
  import_statuses: string[];
  import_limit: number;
  publish_mode?: 'draft' | 'auto'; // 失効（後方互換のため残る）。実際の公開ルールは effective_publish_mode
  effective_publish_mode?: 'manual' | 'auto_add' | 'auto_add_update' | null;
  effective_publish_source?: 'knowledge_loop_settings' | 'default' | null;
  import_watermark?: string | null;
  distill_watermark?: string | null;
  status: ZendeskSourceStatus;
  stats: ZendeskSourceStats;
  last_run_at?: string | null;
  last_error?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  items_total?: number;
  items_pending?: number;
  candidates_pending?: number;
}

export interface ZendeskSourceCreate {
  connection_id?: string;
  subdomain?: string;
  admin_email?: string;
  api_token?: string;
  friendly_name?: string;
  import_since?: string | null;
  import_statuses?: string[];
  import_limit?: number;
  publish_mode?: 'draft' | 'auto';
}

export interface ZendeskCandidate {
  id: string;
  question: string;
  answer: string;
  similar_questions: string[];
  source: { source_type?: string; source_id?: string };
  created_at?: string | null;
}

export const getDatasetZendeskConnections = (datasetId: string) => {
  const { get } = Http.use();
  return get<{ connections: ZendeskConnection[] }>(`/datasets/${datasetId}/zendesk-connections`);
};

export const startZendeskOAuth = (body: {
  subdomain: string;
  connection_id?: string;
  friendly_name?: string;
  client_id?: string;
  client_secret?: string;
}) => {
  const { post } = Http.use();
  return post<{ authorization_url: string; request_id: string }>('/api/auth/zendesk/authorize', body, SILENT);
};

export const getZendeskOAuthSettings = () => {
  const { get } = Http.use();
  return get<{ global_client_available: boolean; redirect_uri: string }>('/api/auth/zendesk/settings', SILENT);
};

export const getZendeskOAuthStatus = (requestId: string) => {
  const { get } = Http.use();
  return get<{ status: 'pending' | 'authorized' | 'failed' | 'expired'; connection_id?: string | null }>(
    `/api/auth/zendesk/status/${requestId}`,
    SILENT
  );
};

export const saveZendeskApiToken = (connectionId: string, body: { admin_email: string; api_token: string }) => {
  const { put } = Http.use();
  return put<ZendeskAuthentication>(`/api/auth/zendesk/connections/${connectionId}/api-token`, body, SILENT);
};

export const getZendeskSources = (datasetId: string) => {
  const { get } = Http.use();
  return get<{ sources: ZendeskSource[] }>(`/datasets/${datasetId}/zendesk-sources`);
};

export const createZendeskSource = (datasetId: string, body: ZendeskSourceCreate) => {
  const { post } = Http.use();
  return post<ZendeskSource>(`/datasets/${datasetId}/zendesk-sources`, body);
};

export const getZendeskSource = (datasetId: string, sourceId: string) => {
  const { get } = Http.use();
  return get<ZendeskSource>(`/datasets/${datasetId}/zendesk-sources/${sourceId}`);
};

export const deleteZendeskSource = (datasetId: string, sourceId: string) => {
  const { delete: deleteHttp } = Http.use();
  return deleteHttp(`/datasets/${datasetId}/zendesk-sources/${sourceId}`);
};

export const importZendeskSource = (datasetId: string, sourceId: string) => {
  const { post } = Http.use();
  return post<{ queued: boolean; task_id?: string | null }>(
    `/datasets/${datasetId}/zendesk-sources/${sourceId}/import`,
    {}
  );
};

export const getZendeskCandidates = (
  datasetId: string,
  sourceId: string,
  params: { limit?: number; offset?: number } = {}
) => {
  const { get } = Http.use();
  return get<{ total: number; items: ZendeskCandidate[] }>(
    `/datasets/${datasetId}/zendesk-sources/${sourceId}/candidates`,
    { params }
  );
};

export const reviewZendeskCandidates = (
  datasetId: string,
  sourceId: string,
  body: { faq_ids: string[]; action: 'approve' | 'reject' }
) => {
  const { post } = Http.use();
  return post<{ updated: number; action: string }>(
    `/datasets/${datasetId}/zendesk-sources/${sourceId}/candidates/review`,
    body
  );
};

// ============ bot 侧（問い合わせ連携：運営画面 対応キュー / 回答差分 / 効果） ============

export type ZendeskDraftStatus =
  | 'generating'
  | 'posted'
  | 'dry_run' // 試運転：生成・引用・判定まで記録し Zendesk には書かない
  | 'inserted'
  | 'answered_as_is'
  | 'answered_edited'
  | 'answered_self'
  | 'discarded'
  | 'failed'
  | 'skipped';
// unanswerable は旧データ（= knowledge_gap）。generation_failed は技術的な失敗で、ナレッジ不足とは分けて数える
export type ZendeskSkipReason =
  | 'form_excluded'
  | 'non_question'
  | 'unanswerable'
  | 'knowledge_gap'
  | 'generation_failed';
// 可答性ゲートで落ちた理由コード（skip_detail）
export type ZendeskSkipDetail =
  | 'ungrounded'
  | 'self_declared_not_found'
  | 'judge_cannot_answer'
  | 'judge_not_found'
  | 'no_answer_marker'
  | 'empty';
export type ZendeskEditReason = 'knowledge_gap' | 'case_specific' | 'tone' | 'policy' | 'out_of_scope';
export type ZendeskReviewStatus = 'pending' | 'in_progress' | 'done';
// 改善タスクの結果（完了時に何をしたか）
export type ZendeskReviewOutcome =
  | 'proposed'
  | 'wording_only'
  | 'case_specific'
  | 'duplicate'
  | 'gap_logged'
  | 'no_action';
export type ZendeskKnowledgeUnitStatus =
  | 'candidate'
  | 'approved'
  | 'scheduled'
  | 'rejected'
  | 'merged'
  | 'superseded'
  | 'stale';

export interface ZendeskDraftSummary {
  id: string;
  ticket_id: number;
  ticket_url?: string | null;
  draft_version: number;
  ticket_subject?: string | null;
  ticket_form_id?: number | null;
  requester_name?: string | null;
  status: ZendeskDraftStatus;
  skip_reason?: ZendeskSkipReason | null;
  similarity?: number | null;
  edit_reason?: ZendeskEditReason | null;
  edit_reason_ai?: ZendeskEditReason | null;
  review_status: ZendeskReviewStatus;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  faq_id?: string | null;
  knowledge_unit_id?: string | null;
  knowledge_unit_status?: ZendeskKnowledgeUnitStatus | null;
  review_outcome?: ZendeskReviewOutcome | null;
  skip_detail?: ZendeskSkipDetail | string | null;
  cited_count?: number;
  note_comment_id?: number | null;
  final_author_name?: string | null;
  last_error?: string | null;
  created_at?: string | null;
  answered_at?: string | null;
  updated_at?: string | null;
}

export interface ZendeskDraftDetail extends ZendeskDraftSummary {
  requester_id?: number | null;
  requester_email?: string | null;
  trigger_comment_id?: number | null;
  question?: string | null;
  draft_text?: string | null;
  final_text?: string | null;
  final_comment_id?: number | null;
  final_author_id?: number | null;
  diff?: { unified?: string; opcodes?: { op: string; draft: string; final: string }[] } | null;
  session_message_id?: string | null;
  distilled_at?: string | null;
  // 実際に引用したナレッジ（試運転の確認・使用状況の追跡）
  cited_refs?: {
    citations?: { type: 'file' | 'url' | string; id: string; title?: string; page?: string | null }[];
    faq_id?: string | null;
    reference_titles?: string[];
    unit_ids?: { id: string; version?: number | null; title?: string }[];
  } | null;
  // 生成時に注入した bot の回答ルール（reply_memory）
  cited_rules?: { id: string; kind: string; text: string }[] | null;
}

export interface ZendeskDraftListParams {
  status?: string; // 逗号分隔
  skip_reason?: string; // 逗号分隔
  ticket_id?: number;
  review_status?: ZendeskReviewStatus;
  review_outcome?: ZendeskReviewOutcome;
  group?: 'ticket'; // 工单顺序（同じチケットの版本が連続する）
  days?: number;
  limit?: number;
  offset?: number;
}

export interface ZendeskRates {
  total: number;
  generated: number;
  answered: number;
  as_is: number;
  edited: number;
  self: number;
  skipped: number;
  as_is_rate: number | null;
  edited_rate: number | null;
  self_rate: number | null;
  generation_rate: number | null;
  unanswerable_rate: number | null;
  generation_failed?: number;
  failed_rate?: number | null;
  knowledge_gap?: number;
}

// 指標の口径：単位 / 分子 / 分母 / 時間フィールド / 時区 / 除外
export interface ZendeskMetricMeta {
  unit: string;
  numerator: string;
  denominator: string;
  time_field: string;
  tz: string;
  excludes?: string[];
}

export interface ZendeskTicketCounts {
  tickets: number;
  answered: number;
  as_is: number;
  edited: number;
  self: number;
  generated: number;
  skipped: number;
}

export interface ZendeskImprovement {
  pending: number;
  in_progress: number;
  done: number;
  outcomes: Partial<Record<ZendeskReviewOutcome, number>>;
  knowledge_gap_open: number;
}

export interface ZendeskKnowledgeCounts {
  approved: number;
  pending: number;
  rejected: number;
  scheduled?: number;
  auto_published?: number;
  backfilled?: number;
  approval_rate: number | null;
  meta?: ZendeskMetricMeta;
}

export interface ZendeskStats extends ZendeskRates {
  days: number;
  since: string;
  tz?: string;
  meta?: Record<string, ZendeskMetricMeta>;
  tickets?: ZendeskTicketCounts;
  improvement?: ZendeskImprovement;
  by_status: Record<string, number>;
  by_skip_reason: Record<string, number>;
  previous: ZendeskRates;
  similarity_buckets: Record<string, number>;
  similarity_avg: number | null;
  weekly: { week: string; answered: number; as_is: number; as_is_rate: number | null }[];
  daily: {
    day: string;
    answered: number;
    as_is: number;
    as_is_rate: number | null;
    generated: number;
    skipped: number;
  }[];
  edit_reasons: Record<string, number>;
  review: Record<ZendeskReviewStatus, number>;
  knowledge: ZendeskKnowledgeCounts;
}

export const listZendeskDrafts = (robotId: string, params: ZendeskDraftListParams) => {
  const { get } = Http.use();
  return get<{ total: number; items: ZendeskDraftSummary[] }>(`/robots/${robotId}/zendesk/drafts`, { params });
};

export const getZendeskDraft = (robotId: string, draftId: string) => {
  const { get } = Http.use();
  return get<ZendeskDraftDetail>(`/robots/${robotId}/zendesk/drafts/${draftId}`);
};

export const patchZendeskDraft = (
  robotId: string,
  draftId: string,
  body: {
    edit_reason?: ZendeskEditReason | '';
    review_status?: ZendeskReviewStatus;
    reviewed_by?: string;
    review_outcome?: ZendeskReviewOutcome | '';
  }
) => {
  const { patch } = Http.use();
  return patch<{
    id: string;
    edit_reason: string | null;
    review_status: ZendeskReviewStatus;
    reviewed_by: string | null;
    review_outcome?: ZendeskReviewOutcome | null;
  }>(`/robots/${robotId}/zendesk/drafts/${draftId}`, body);
};

export interface ZendeskProposeResult {
  decision: 'add' | 'update' | 'conflict' | 'noop' | 'skipped';
  unit_id: string | null;
  unit?: { id: string; title: string; status: ZendeskKnowledgeUnitStatus; proposal_type: string } | null;
  dataset_id: string;
  run_id?: string;
  skip_samples?: { source_ref: string; reason: string }[];
  reused: boolean;
}

/** 「ナレッジ改善を提案」：担当者の回答を毎日のナレッジ抽出と同じ判定（新規 / 更新 / 既知 / 競合）で台帳へ */
export const proposeZendeskDraft = (robotId: string, draftId: string, body: { dataset_id?: string } = {}) => {
  const { post } = Http.use();
  return post<ZendeskProposeResult>(`/robots/${robotId}/zendesk/drafts/${draftId}/propose`, body, SILENT);
};

export const zendeskDraftToFaq = (
  robotId: string,
  draftId: string,
  body: { question?: string; answer?: string; dataset_id?: string } = {}
) => {
  const { post } = Http.use();
  return post<{ id: string; faq_id: string; dataset_id: string }>(
    `/robots/${robotId}/zendesk/drafts/${draftId}/to-faq`,
    body,
    SILENT
  );
};

export const getZendeskStats = (robotId: string, days = 30) => {
  const { get } = Http.use();
  return get<ZendeskStats>(`/robots/${robotId}/zendesk/stats`, { params: { days } });
};
